/*
 * useBoxReads — what each of some boxes answers on one path of its own API,
 * asked through the server: every `every` ms while the page is visible, or
 * once for 0, and at once whenever the boxes change. `ask(ids)` asks those
 * again now. Answers {id: {body, at}}, body null where the box did not answer;
 * a box not asked yet is absent.
 *
 * ! file hooks/useBoxReads.js
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import useVisibleInterval from "./useVisibleInterval";

export default function useBoxReads(ids, path, every = 0) {
  const [reads, setReads] = useState({});
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const ask = useCallback(async (which) => {
    const got = await Promise.all(which.filter(Boolean).map(async (id) => {
      const body = await fetchWithTimeout(nodeApiUrlFor(id)(path), { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      return [id, { body, at: new Date() }];
    }));
    if (live.current) setReads((was) => ({ ...was, ...Object.fromEntries(got) }));
  }, [path]);
  const key = ids.filter(Boolean).join(",");
  const all = useCallback(() => (key ? ask(key.split(",")) : undefined), [ask, key]);
  const polled = every > 0;
  useVisibleInterval(all, every, polled && !!key);
  // The interval asks at once when it starts; after that, and always when
  // nothing polls, a change of boxes asks here.
  const started = useRef(polled);
  useEffect(() => {
    if (started.current) {
      started.current = false;
      return;
    }
    all();
  }, [all]);
  return { reads, ask };
}
